import { workAreaRoutePath } from '@featit/shared/addressing/paths';
import { type PartStatusSegmentType } from '@featit/shared/mcp-parts';
import { BLOCKED_STATUS_KEY } from '@featit/shared/notification-severity';
import { WAITING_STATUS } from '@featit/shared/waiting-status';
import { STATE_GROUP_ORDER } from '@featit/shared/work-items';
import {
  NO_RELEASE_FILTER,
  UNASSIGNED_RESPONSIBLE_FILTER,
} from '@/features/work-items/serverListFilter';
import {
  ANY,
  ANY_STATUS,
  DEFAULT_PART_ITEM_FILTER,
  NONE,
  NOT_DONE,
  type IPartItemFilter,
} from './partItemFilterDefaults';

/**
 * The Parts tree of one work area (F-0321). Scoped only: parts belong to their leading work area
 * (`D-0206`), so there is no unscoped `/-/parts`.
 */
export function projectPartsPath(projectSlug: string): string {
  return workAreaRoutePath(projectSlug, '/parts');
}

/**
 * A part's page, by handle. The handle is one path segment, its slashes encoded
 * (`desktop/bridge` → `/acme/featit/-/parts/desktop%2Fbridge`): a splat route lost to the deepest
 * namespace address, which is as many segments long, so a handle three deep opened "No page here".
 */
export function projectPartPath(projectSlug: string, handle: string): string {
  return workAreaRoutePath(projectSlug, `/parts/${encodeURIComponent(handle)}`);
}

/** The Work items list's status groups that are not done: the tree's "Not done". */
const NOT_DONE_STATE_GROUPS = STATE_GROUP_ORDER.filter(
  (group) => group !== 'completed' && group !== 'cancelled',
);

/**
 * The Work items quick view filtered to a part, or to No part, under the tree's filters (F-0334;
 * Jay, 2026-09-30: *"users should be able to click on ** on no part and jump to the respective filter
 * in work items"*): the quick view's own part address (`partQuickViewUrl.ts`) with its own filter
 * params, so the list opens on exactly the items the Parts tree counted.
 *
 * - Status: *Not done* is the list's not-done status groups (`state=`) with the waiting items kept
 *   (`waiting=include`); one status is `status=<key>`; *Any status* says nothing.
 * - Responsible: `resp=<user:…|agent:…>`, or `resp=none` for Unassigned.
 * - Release: the target release, `release=<id>`, or `release=none`.
 */
export function partItemsListPath(
  projectSlug: string,
  part: { id: string } | 'none',
  {
    scope = 'including-below',
    filter = DEFAULT_PART_ITEM_FILTER,
  }: {
    scope?: 'including-below' | 'this';
    filter?: IPartItemFilter;
  } = {},
): string {
  const params = new URLSearchParams();
  params.set('part', part === 'none' ? 'none' : part.id);
  if (part !== 'none' && scope === 'this') params.set('pscope', 'this');
  if (filter.status === NOT_DONE) {
    // A waiting item is not done (D-0159: it is blocked until the wait resolves), so it is counted
    // here, and the list is asked to keep it under its not-done groups.
    params.set('state', NOT_DONE_STATE_GROUPS.join(','));
    params.set('waiting', 'include');
  } else if (filter.status !== ANY_STATUS) params.set('status', filter.status);
  if (filter.responsible === NONE) params.set('resp', UNASSIGNED_RESPONSIBLE_FILTER);
  else if (filter.responsible !== ANY) params.set('resp', filter.responsible);
  if (filter.release === NONE) params.set('release', NO_RELEASE_FILTER);
  else if (filter.release !== ANY) params.set('release', filter.release);
  return `${workAreaRoutePath(projectSlug, '/items')}?${params.toString().replace(/%2C/g, ',')}`;
}

/** What one figure in a status column stands for: its total, one segment, or Blocked (F-0546). */
export type PartStatusFigureType = 'total' | PartStatusSegmentType | 'blocked';

/**
 * The Work items list a figure in the Parts tree's status columns opens (F-0546, PT-46): the part
 * with everything below it, under the tree's filters, narrowed to the figure — so the list draws
 * the number that was clicked.
 *
 * - *total*: the tree's own filters ({@link partItemsListPath}).
 * - a status group: `state=<group>`, which leaves the waiting items out — they are their own
 *   segment — with one chosen status kept as `status=`.
 * - *Waiting*: `status=waiting`, with the not-done groups and `waiting=include` under *Not done*, so
 *   a finished item that still waits is left out as the count leaves it out.
 * - *Blocked*: `status=blocked`, the same way.
 * - `roadmapReleaseIds`, for the *Next release* column: `roadmap=<id>[,<id>…]`, the releases the
 *   roadmap draws the items under (fixed-in, else an open target), beside the tree's own filters.
 */
export function partStatusListPath(
  projectSlug: string,
  part: { id: string },
  figure: PartStatusFigureType,
  filter: IPartItemFilter = DEFAULT_PART_ITEM_FILTER,
  roadmapReleaseIds?: readonly string[],
): string {
  const base = new URL(partItemsListPath(projectSlug, part, { filter }), 'http://x');
  const params = base.searchParams;
  if (roadmapReleaseIds && roadmapReleaseIds.length > 0) {
    params.set('roadmap', roadmapReleaseIds.join(','));
  }
  if (figure === 'waiting' || figure === 'blocked') {
    params.delete('status');
    params.set('status', figure === 'waiting' ? WAITING_STATUS.key : BLOCKED_STATUS_KEY);
  } else if (figure !== 'total') {
    params.delete('waiting');
    params.set('state', figure);
  }
  return `${base.pathname}?${params.toString().replace(/%2C/g, ',')}`;
}
